import React, { useEffect, useState, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from 'state/axios';
import toast from 'react-hot-toast';
import { Formik } from 'formik';
import * as yup from 'yup';
import { useTheme, useMediaQuery, Box, TextField, Button, 
    CircularProgress, Typography, 
} from '@mui/material';
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns'
import { de, enGB } from 'date-fns/locale';
import ArrowBackIosNewIcon from '@mui/icons-material/ArrowBackIosNew';
import useAuthCheck from 'hooks/useAuthCheck';
import { LAYOUT_HEIGHT } from 'state/constants';
import { parseUTCDate } from 'state/helpers';
import CustomDatePicker from "components/CustomDatePicker";
import FlexBetween from 'components/FlexBetween';
import FlexCenter from 'components/FlexCenter';
import Header from 'components/Header';
import StyledButton from 'components/StyledButton';


const FixedBlockTimePage = () => {
    useAuthCheck();
    const { fixedBlockTimeId } = useParams();    
    const navigate = useNavigate();
    const locales = { 'en-us': undefined, 'en-gb': enGB, de };
    const theme = useTheme();
    const isDesktop = useMediaQuery(theme.breakpoints.up('lg'));

    const [loading, setLoading] = useState(false);
    const [error, setError] = useState(null);

    const [formValues, setFormValues] = useState({
        sector: '',
        acTypes: '',
        blockTime: 0,
        validSince: null,
        validUntil: null,        
    });
    

    const fetchFixedBlockTime = useCallback(async () => {
        if (fixedBlockTimeId) {
            const url = `/fixedblocktimes/${fixedBlockTimeId}`;
            return await api.get(url);            
        }
    }, [fixedBlockTimeId]);
    
    useEffect(() => {
        const fetchData = async () => {
            try {
                setError(null);
                setLoading(true);
                
                const [
                    fixedBlockTimeResponse,
                ] = await Promise.all([
                    fixedBlockTimeId ? fetchFixedBlockTime() : Promise.resolve(null),
                ]);
                
                const formData = fixedBlockTimeResponse?.data || {};             
                setFormValues({
                    sector: formData.sector || "",
                    acTypes: formData.acTypes || "",
                    blockTime: formData.blockTime || 0,
                    validSince: formData.validSince ? new Date(formData.validSince) : null,
                    validUntil: formData.validUntil ? new Date(formData.validUntil) : null,
                });                
            } catch (error) {
                setError(error);
                toast.error(error.response?.data?.message || "Failed to fetch data!");
            } finally {
                setLoading(false);
            }
        };
    
        fetchData();      
    }, [fetchFixedBlockTime, 
        fixedBlockTimeId,
    ]); 
    
    const checkoutSchema = yup.object().shape({
        sector: yup.string().required('Required'),
        acTypes: yup.string().required('Required'),
        blockTime: yup
            .number()
            .typeError('Block Time must be a number')
            .required('Required'),
        validSince: yup.date().required('Required'),
        validUntil: yup.date().nullable(), 
    });

    const handleFormSubmit = async (values, onSubmitProps) => {
        try {
            const method = fixedBlockTimeId ? "put" : "post";
            const url = fixedBlockTimeId
                ? `/fixedblocktimes/update/${fixedBlockTimeId}` 
                : '/fixedblocktimes/new'
            
            const payload = {
                ...values,
                validSince: values.validSince
                    ? parseUTCDate(values.effectiveFrom) 
                    : null,
                validUntil: values.validUntil
                    ? parseUTCDate(values.validUntil) 
                    : null,
            };

            await api({
                method,
                url,
                data: payload,
            });

            toast.success(fixedBlockTimeId 
                ? 'Fixed block time updated successfully.' 
                : 'Fixed block time created successfully.'
            );

            if (!fixedBlockTimeId) {
                onSubmitProps.resetForm();
            }

            navigate("/fixedblocktimes");
        } catch (error) {
            toast.error(error?.response?.data?.message || "Failed to save fixed block time!");
        }
    };
    

    return (
        <Box className="main-container-box">                
            <FlexBetween>
                <Header
                    title="Fixed Block Times"
                    subtitle={`${loading ? 'Loading...' : 
                        fixedBlockTimeId 
                            ? 'Update' 
                            : 'Create'}`
                    }
                />
                {!loading && (
                    <Box display="flex" alignItems="center" gap={1}>
                        <StyledButton 
                            onClick={() => navigate("/fixedblocktimes")}
                        >
                            <ArrowBackIosNewIcon />
                        </StyledButton>
                    </Box>
                )}
            </FlexBetween>
            {loading ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <CircularProgress />
                </FlexCenter>
            ) : error ? (
                <FlexCenter height={LAYOUT_HEIGHT}>
                    <Typography color="error">Error: {error?.message || ''}</Typography>
                </FlexCenter>
            ) : (
                <Box height={LAYOUT_HEIGHT} sx={{ overflowY: 'auto' }}>
                    <LocalizationProvider 
                        dateAdapter={AdapterDateFns}
                        adapterLocale={locales['en-gb']}
                    > 
                        <Formik
                            initialValues={formValues}
                            enableReinitialize={true}
                            validationSchema={checkoutSchema}
                            onSubmit={handleFormSubmit}
                        >
                            {({
                                values,
                                errors,
                                touched,
                                handleBlur,
                                handleChange,
                                handleSubmit,
                                setFieldValue,
                            }) => (
                                <form onSubmit={handleSubmit}>
                                    <Box
                                        display="grid"
                                        gap={2}
                                        gridTemplateColumns="repeat(12, minmax(0, 1fr))"
                                        sx={{
                                            '& > div': { gridColumn: isDesktop ? undefined : 'span 12' },
                                        }}
                                    >
                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            type="text"
                                            label="Sector"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.sector}
                                            name="sector"
                                            error={!!touched.sector && !!errors.sector}
                                            helperText={touched.sector && errors.sector}
                                            sx={{ gridColumn: 'span 12' }}
                                        />

                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            type="text"
                                            label="AC Types"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.acTypes}
                                            name="acTypes"
                                            error={!!touched.acTypes && !!errors.acTypes}
                                            helperText={touched.acTypes && errors.acTypes}
                                            sx={{ gridColumn: 'span 12' }}
                                        />

                                        <TextField
                                            fullWidth
                                            required
                                            variant="filled"
                                            type="number"
                                            label="Block Time"
                                            onBlur={handleBlur}
                                            onChange={handleChange}
                                            value={values.blockTime}
                                            name="blockTime"
                                            error={!!touched.blockTime && !!errors.blockTime}
                                            helperText={touched.blockTime && errors.blockTime}
                                            slotProps={{
                                                input: {
                                                    readOnly: true,
                                                    sx: {
                                                        "& input": { textAlign: "right" },
                                                    },
                                                },
                                            }} 
                                            sx={{ gridColumn: 'span 12' }}
                                        />

                                        <CustomDatePicker
                                            label="Valid Since"
                                            name="validSince"
                                            value={values.validSince}
                                            maxDate={values.validUntil}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <CustomDatePicker
                                            label="Valid Until"
                                            name="validUntil"
                                            value={values.validUntil}
                                            minDate={values.validSince}
                                            onChange={setFieldValue}
                                            touched={touched}
                                            errors={errors}
                                            sx={{ gridColumn: "span 6" }}
                                        />

                                        <Button
                                            fullWidth
                                            type="submit"
                                            variant="contained"
                                            color="secondary"
                                            sx={{ gridColumn: "span 12", height: 42 }}
                                        >
                                            {fixedBlockTimeId ? "Update" : "Create"}
                                        </Button>
                                    </Box>
                                </form>
                            )}
                        </Formik>
                    </LocalizationProvider>
                </Box>
            )}
        </Box>
    );
};

export default FixedBlockTimePage;